import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCart } from "./CartContext";

function ProductDetail({ products }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const product = products.find((p) => p.id === id);

  if (!product) {
    return (
      <div className="container text-center mt-8">
        <i className="material-symbols-rounded" style={{ fontSize: "4rem", color: "#ccc" }}>search_off</i>
        <h4 className="mt-3">Product not found</h4>
        <button className="btn bg-gradient-dark mt-3" onClick={() => navigate("/")}>
          Back to Store
        </button>
      </div>
    );
  }

  const defaultPrice = product.default_price;
  const unitAmount = defaultPrice && typeof defaultPrice === "object" ? defaultPrice.unit_amount : 0;
  const priceId = defaultPrice && typeof defaultPrice === "object" ? defaultPrice.id : defaultPrice;
  const currency = (defaultPrice && typeof defaultPrice === "object" ? defaultPrice.currency : "usd") || "usd";
  const priceFormatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format((unitAmount || 0) / 100);

  const metadata = product.metadata || {};
  const colors = metadata.colors ? metadata.colors.split(",").map((c) => c.trim()) : [];
  const sizes = metadata.sizes ? metadata.sizes.split(",").map((s) => s.trim()) : [];
  const careInstructions = metadata.care_instructions ? metadata.care_instructions.split("|").map((c) => c.trim()) : [];

  const handleAddToCart = () => {
    addToCart({
      id: product.id,
      priceId,
      name: product.name,
      price: unitAmount,
      image: product.images && product.images[0],
    });
    navigate(-1);
  };

  return (
    <div>
      <div className="d-flex align-items-center px-4 py-2 border-bottom">
        <button
          className="btn btn-link p-0 text-dark me-3"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <i className="material-symbols-rounded">arrow_back</i>
        </button>
        <h5 className="mb-0 font-weight-bold">{product.name}</h5>
      </div>

      <div className="container my-5">
        <div className="row">
          <div className="col-lg-5 mb-4 mb-lg-0">
            {product.images && product.images[0] ? (
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-100 border-radius-lg shadow"
                style={{ objectFit: "cover", maxHeight: "500px" }}
              />
            ) : (
              <div
                className="d-flex align-items-center justify-content-center border-radius-lg bg-light"
                style={{ height: "300px" }}
              >
                <i className="material-symbols-rounded text-muted" style={{ fontSize: "4rem" }}>image</i>
              </div>
            )}
            {product.images && product.images.length > 1 && (
              <div className="d-flex gap-2 mt-3">
                {product.images.slice(1).map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`${product.name} view ${i + 2}`}
                    className="border-radius-lg"
                    style={{ width: "72px", height: "72px", objectFit: "cover" }}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="col-lg-7">
            <h2 className="font-weight-bold">{product.name}</h2>
            <span className="badge badge-success mb-3">In Stock</span>
            <h3 className="text-dark mb-4">{priceFormatted}</h3>

            {product.description && (
              <p className="text-muted mb-4">{product.description}</p>
            )}

            {colors.length > 0 && (
              <div className="mb-3">
                <label className="form-label font-weight-bold">Color</label>
                <select className="form-control">
                  {colors.map((c, i) => (
                    <option key={i} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            )}

            {sizes.length > 0 && (
              <div className="mb-3">
                <label className="form-label font-weight-bold">Size</label>
                <select className="form-control">
                  {sizes.map((s, i) => (
                    <option key={i} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            )}

            {metadata.designer_info && (
              <div className="mb-3">
                <p className="text-sm font-weight-bold mb-1">Designer Information</p>
                <p className="text-sm text-muted">{metadata.designer_info}</p>
              </div>
            )}

            {careInstructions.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-weight-bold mb-1">Details &amp; Care</p>
                <ul className="text-sm text-muted ps-3">
                  {careInstructions.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            <button
              className="btn bg-gradient-dark w-100 mt-2"
              type="button"
              onClick={handleAddToCart}
            >
              <i className="material-symbols-rounded me-2">add_shopping_cart</i>
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;
